import { queryOptions, useQuery } from '@tanstack/react-query';
import { includes } from 'rambda';
import { useEffect } from 'react';
import { useCommDispatcher } from '@/hooks/comm/use-comm';
import type { ConversationMessageTypeUnion } from '@/models/conversations/conversation';
import {
  type ConversationGroupType,
  type ConversationSessionsTypeUnion,
  type ConversationSessionType,
  type SessionDataGroupType,
  type SessionDataResponseType,
  type SessionDataSessionType,
  type SessionDataTypeUnion,
  type SessionListResponseType,
  sessionDataResponseSchema,
  sessionListResponseSchema,
} from '@/models/conversations/conversation-session';
import { generateResponseScheme, type RespondedWith } from '@/models/response';
import { promisifySessionsHydration, useConversationSessionStore } from '@/stores/conversation-session';
import { addMessages } from '@/stores/message';
import { fetcher } from '@/utils/fetcher';
import { transformMessage } from '@/utils/message';
import { queryClient } from '@/utils/query-client';
import { generateSessionKey, sortSessions } from '@/utils/session';

interface SessionInfoType {
  SessionType: '1' | '2';
  ID: number;
  FromID: number;
}

function generateSessionInfos(
  remoteSessions: SessionListResponseType,
  storedSessions: ConversationSessionsTypeUnion[] = [],
) {
  const { lastReadIds } = useConversationSessionStore.getState();

  return [...remoteSessions.ChatSessions, ...remoteSessions.Groups].reduce<SessionInfoType[]>(
    (sessionInfos, remoteSession) => {
      const sessionType = remoteSession.SessionType === 'session' ? '1' : '2';
      const key = generateSessionKey(remoteSession.SessionType, remoteSession.ID);
      const lastReadId = lastReadIds[key];
      const storedSession = storedSessions.find((session) => session.ID === remoteSession.ID);

      if (!storedSession) {
        sessionInfos.push({
          SessionType: sessionType,
          ID: remoteSession.ID,
          FromID: 0,
        });
      } else if (lastReadId !== remoteSession.LastMsgID) {
        sessionInfos.push({
          SessionType: sessionType,
          ID: remoteSession.ID,
          FromID: lastReadId ?? 0,
        });
      }

      return sessionInfos;
    },
    [],
  );
}

function mergeSessionResponses(
  listResponse: SessionListResponseType,
  dataResponse: SessionDataResponseType,
  storedSessions: ConversationSessionsTypeUnion[] = [],
): ConversationSessionsTypeUnion[] {
  const dataSessionMap = [...dataResponse.ChatSessions, ...dataResponse.Groups].reduce<
    Record<string, SessionDataTypeUnion>
  >((accu, session) => {
    accu[generateSessionKey(session.SessionType, session.ID)] = session;
    return accu;
  }, {});
  const storedSessionMap = storedSessions.reduce(
    (accu, session) => {
      accu[generateSessionKey(session.SessionType, session.ID)] = session;
      return accu;
    },
    {} as Record<string, ConversationSessionsTypeUnion>,
  );

  return [
    ...listResponse.ChatSessions.map<ConversationSessionType>((session) => {
      const key = generateSessionKey(session.SessionType, session.ID);
      const data = dataSessionMap[key] as SessionDataSessionType;
      const stored = storedSessionMap[key];
      const shouldUseStoredSession = getShouldUseStoredSession(stored, data);

      return {
        ...session,
        UnReadCount: (shouldUseStoredSession ? stored.UnReadCount : data.UnReadCount) ?? 0,
        UnReadMaxID: (shouldUseStoredSession ? stored.UnReadMaxID : data.UnReadMaxID) ?? 0,
        Chats: data?.Chats ?? [],
      };
    }),
    ...listResponse.Groups.map<ConversationGroupType>((session) => {
      const key = generateSessionKey(session.SessionType, session.ID);
      const data = dataSessionMap[key] as SessionDataGroupType;
      const stored = storedSessionMap[key];
      const shouldUseStoredSession = getShouldUseStoredSession(stored, data);

      return {
        ...session,
        UnReadCount: (shouldUseStoredSession ? stored.UnReadCount : data.UnReadCount) ?? 0,
        UnReadMaxID: (shouldUseStoredSession ? stored.UnReadMaxID : data.UnReadMaxID) ?? 0,
        GotAt: (session.ReadGroupChatID < session.LastMsgID ? data?.GotAt : false) ?? false,
        Chats: data?.Chats ?? [],
      };
    }),
  ];
}

function getShouldUseStoredSession(
  stored: ConversationSessionsTypeUnion | undefined,
  remote: SessionDataTypeUnion | undefined,
): stored is ConversationSessionsTypeUnion {
  if (!stored) return false;
  if (!remote) return true;

  if (stored.LastMsgID >= remote.LastMsgID) return true;

  return stored.UnReadCount >= remote.UnReadCount;
}

async function getAllSessionList(signal: AbortSignal) {
  const response = await fetcher<RespondedWith<SessionListResponseType>>('/GetAllSessionList', undefined, {
    signal,
    schema: generateResponseScheme(sessionListResponseSchema),
    validResponseCodes: [0, 1],
  });

  return response?.Data;
}

async function getAllSessionData(sessionInfos: SessionInfoType[], signal: AbortSignal) {
  if (sessionInfos.length === 0)
    return {
      ChatSessions: [],
      Groups: [],
    };

  const response = await fetcher<RespondedWith<SessionDataResponseType>>(
    '/GetAllSessionData',
    { SessionInfos: sessionInfos },
    {
      signal,
      schema: generateResponseScheme(sessionDataResponseSchema),
      validResponseCodes: [0, 1],
    },
  );

  return response?.Data;
}

export const allChatSessionQueryOptions = queryOptions({
  queryKey: ['/GetAllChatSession'],
  queryFn: async ({ signal, queryKey }): Promise<ConversationSessionsTypeUnion[]> => {
    await promisifySessionsHydration();

    const cachedData = queryClient.getQueryData<ConversationSessionsTypeUnion[]>(queryKey) ?? [];

    const sessionList = await getAllSessionList(signal);
    if (!sessionList) return cachedData;

    const sessionInfos = generateSessionInfos(sessionList, cachedData);
    if (sessionInfos.length === 0)
      return mergeSessionResponses(sessionList, { ChatSessions: [], Groups: [] }, cachedData);

    const sessionData = await getAllSessionData(sessionInfos, signal);
    if (!sessionData) return cachedData;

    const data = mergeSessionResponses(sessionList, sessionData, cachedData);

    for (const session of data) {
      if (session.UnReadCount === 0) {
        useConversationSessionStore
          .getState()
          .setLastRead(generateSessionKey(session.SessionType, session.ID), session.LastMsgID)
          .catch(console.warn);
      }
    }
    const messages = data.flatMap<ConversationMessageTypeUnion>((session) => session.Chats ?? []).map(transformMessage);
    addMessages(messages).catch(console.warn);

    return data;
  },
  placeholderData: [],
  staleTime: 0,
});

export default function useConversationSessions() {
  const { hiddenSessions } = useConversationSessionStore();

  const query = useQuery({
    ...allChatSessionQueryOptions,
    select: (data): ConversationSessionsTypeUnion[] => {
      if (!data) return [];

      return sortSessions(
        data.filter(({ SessionType, ID }) => !hiddenSessions.includes(generateSessionKey(SessionType, ID))),
      );
    },
  });

  return {
    availableSessions: query.data ?? [],
    ...query,
  };
}

export function useSessionUnreadCount(shouldAnnounce = false) {
  const { dispatch } = useCommDispatcher();
  const { hiddenSessions } = useConversationSessionStore();

  const query = useQuery({
    ...allChatSessionQueryOptions,
    select: (data): number => {
      if (!data) return 0;

      return data.reduce((sum, session) => {
        const key = generateSessionKey(session.SessionType, session.ID);

        if (session.IsNotDisturb || hiddenSessions.includes(key)) return sum;

        return sum + (session.UnReadCount === Number.POSITIVE_INFINITY ? 1 : session.UnReadCount);
      }, 0);
    },
  });

  useEffect(() => {
    if (!shouldAnnounce) return;

    dispatch({
      event: 'new-message',
      data: { count: query.data ?? 0 },
    });
  }, [query.data, dispatch, shouldAnnounce]);

  return {
    unreadCount: query.data ?? 0,
    ...query,
  };
}

export const useSubscribedSessionUnReadCountByUserId = () => {
  const { dispatch } = useCommDispatcher();
  const { subscribedSessions } = useConversationSessionStore();

  const hasSubscribedSessions = subscribedSessions.length > 0;

  const subscribeCountQuery = useQuery({
    ...allChatSessionQueryOptions,
    select: (data): { userId: number; count: number }[] => {
      if (!data)
        return subscribedSessions.map((session) => {
          return { userId: session.userId, count: 0 };
        });
      const result: { userId: number; count: number }[] = [];
      data.forEach((session) => {
        if (
          session.UserID &&
          includes({ userId: session.UserID }, subscribedSessions) &&
          session.SessionType === 'session'
        ) {
          result.push({
            userId: session.UserID,
            count: session.UnReadCount === Number.POSITIVE_INFINITY ? 1 : session.UnReadCount,
          });
        }
      });

      return result;
    },
    enabled: hasSubscribedSessions,
  });

  useEffect(() => {
    if (hasSubscribedSessions && subscribeCountQuery.data && subscribeCountQuery.data.length > 0) {
      subscribeCountQuery.data.forEach((data) => {
        dispatch({
          event: 'new-message',
          data: {
            session: {
              type: 'user',
              id: data.userId,
            },
            count: data.count,
          },
        });
      });
    }
  }, [dispatch, hasSubscribedSessions, subscribeCountQuery.data]);

  return { hasSubscribedSessions, counts: subscribeCountQuery.data ?? [] };
};
